import React from 'react';
import { Button } from '@/ui/components/button';
import MetaData from '../meal-details/components/meta-data';
import AdUnit from '../../components/ad-unit';
import Link from './Link';
import { TemplatedLazyImage } from '../../components/image';
import {
    Breadcrumbs,
    ContactCard,
    DeliverySection,
    DirectionsButton,
    HoursCard,
    useRestaurantHours,
    LocationSection,
    PageFrame,
    RestaurantCard,
    TwoColumns,
    correctionTriggerClass,
    type DeliveryProvider,
} from './parts';
import type { Address, OpenHoursRow } from './format';
import { idFromPath } from '../../components/report-correction/correction';
import { usePageView } from './views';

/**
 * The meal page rebuilt in the new framework (W3.1), used in place of
 * meal-details/View when ENABLE_NEW_DETAIL_PAGES is on at build time. Same query
 * fragments, head tags (the original MetaData), status codes, loading/error
 * states and owner/correction gating as the original; see
 * docs/parity/w3-1-detail-pages.md.
 */

// Required inside DefinePlugin-constant branches, as on the original page.
const MealCorrectionLink:
    | typeof import('../../components/report-correction/ReportCorrectionLink').MealCorrectionLink
    | null =
    process.env.ENABLE_CORRECTIONS && process.env.ENABLE_ACCOUNTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/report-correction/ReportCorrectionLink')
              .MealCorrectionLink
        : null;

// Lists (prototype SaveToListModal): only with ENABLE_LISTS and ENABLE_ACCOUNTS.
const SaveButton:
    typeof import('../../components/lists/SaveButton').SaveButton | null =
    process.env.ENABLE_LISTS && process.env.ENABLE_ACCOUNTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/lists/SaveButton').SaveButton
        : null;

const ClaimBanner: typeof import('./ClaimEntries').ClaimBanner | null = process
    .env.ENABLE_FOR_RESTAURANTS
    ? // eslint-disable-next-line @typescript-eslint/no-require-imports
      require('./ClaimEntries').ClaimBanner
    : null;

export interface MealPageData {
    meal: {
        name: string;
        path?: string | null;
        description?: string | null;
        price?: { display?: string | null } | null;
        mainImage?: { templatedUrl?: string | null } | null;
        restaurant: {
            name: string;
            path?: string | null;
            website?: string | null;
            phone?: string | null;
            address?: Address | null;
            openHours?: OpenHoursRow[] | null;
            delivery?: DeliveryProvider[] | null;
        };
    } | null;
    error?: { message?: string } | null;
}

const StateMessage = ({ children }: { children: React.ReactNode }) => (
    <>
        <MetaData data={{ meal: null }} />
        <PageFrame>
            <div className="mx-auto max-w-[600px] py-12 text-center">
                {children}
            </div>
        </PageFrame>
    </>
);

const MealPage = ({
    data,
    location,
    error,
}: {
    data: MealPageData | null | undefined;
    location?: { pathname?: string };
    error?: { message?: string } | null;
}) => {
    const hours = useRestaurantHours(idFromPath(data?.meal?.restaurant?.path));
    usePageView(
        idFromPath(data?.meal?.restaurant?.path),
        idFromPath(location?.pathname),
    );
    // Kept from the original View: an error passed in, or one in the data.
    if (error || data?.error) {
        const info = (error || data?.error)!;
        return (
            <StateMessage>
                <h1 className="mb-4 text-title-1 font-semibold text-ink-strong">
                    Oops! Something went wrong
                </h1>
                <p className="mb-6 text-body-1 text-ink-muted">
                    {info.message ||
                        'Unable to load meal details. Please try again later.'}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <Button onClick={() => window.location.reload()}>
                        Try Again
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => window.history.back()}
                    >
                        Go Back
                    </Button>
                </div>
            </StateMessage>
        );
    }

    if (!data || !data.meal) {
        return (
            <StateMessage>
                <p role="status" className="text-body-1 text-ink-muted">
                    Loading meal details...
                </p>
            </StateMessage>
        );
    }

    const { meal } = data;
    const { restaurant } = meal;

    return (
        <>
            <MetaData data={data} />
            <PageFrame>
                <Breadcrumbs
                    items={[
                        { label: 'Home', to: '/' },
                        {
                            label: restaurant.name,
                            to: restaurant.path ?? undefined,
                        },
                        { label: meal.name },
                    ]}
                />
                <TwoColumns
                    main={
                        <>
                            <header>
                                {restaurant.path && (
                                    <p className="mb-1 text-overline font-medium tracking-wide uppercase">
                                        <Link
                                            to={restaurant.path}
                                            className="rounded-sm text-ink-muted no-underline outline-none hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                                        >
                                            {restaurant.name}
                                        </Link>
                                    </p>
                                )}
                                <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between md:gap-6">
                                    <h1 className="text-title-1 font-semibold break-words text-ink-strong md:text-large-title">
                                        {meal.name}
                                    </h1>
                                    {meal.price?.display && (
                                        <p className="shrink-0 text-title-2 font-semibold text-ink md:text-title-1">
                                            <span className="sr-only">
                                                Price:{' '}
                                            </span>
                                            {meal.price.display}
                                        </p>
                                    )}
                                </div>
                                {SaveButton && (
                                    <div className="mt-4">
                                        <SaveButton
                                            legacyMealId={idFromPath(
                                                location?.pathname,
                                            )}
                                            itemName={meal.name}
                                        />
                                    </div>
                                )}
                            </header>
                            {meal.mainImage?.templatedUrl && (
                                <div className="max-h-[30rem] overflow-hidden rounded-lg bg-surface-subtle md:aspect-[4/3]">
                                    <TemplatedLazyImage
                                        templatedUrl={
                                            meal.mainImage.templatedUrl
                                        }
                                        size="1280x720"
                                        options="fit=cover"
                                        alt={meal.name}
                                        title={meal.name}
                                        className="block size-full object-cover"
                                    />
                                </div>
                            )}
                            {meal.description && (
                                <p className="text-body-1 text-ink">
                                    {meal.description}
                                </p>
                            )}
                            {ClaimBanner && (
                                <ClaimBanner
                                    restaurantName={restaurant.name}
                                    restaurantPath={restaurant.path}
                                />
                            )}
                            <LocationSection
                                name={restaurant.name}
                                address={restaurant.address}
                            />
                            <DeliverySection delivery={restaurant.delivery} />
                        </>
                    }
                    aside={
                        <>
                            <AdUnit
                                slot="meal-details-sidebar"
                                format="rectangle"
                                sticky={true}
                            />
                            <RestaurantCard
                                name={restaurant.name}
                                path={restaurant.path}
                                address={restaurant.address}
                            />
                            <ContactCard
                                phone={restaurant.phone}
                                website={restaurant.website}
                            />
                            <HoursCard
                                openHours={restaurant.openHours}
                                hours={hours}
                                showStatus
                            />
                            <DirectionsButton address={restaurant.address} />
                            {MealCorrectionLink && (
                                <MealCorrectionLink
                                    meal={meal}
                                    pathname={location?.pathname}
                                    appearance="button"
                                    triggerClassName={correctionTriggerClass}
                                />
                            )}
                        </>
                    }
                />
            </PageFrame>
        </>
    );
};

export default MealPage;
